
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, NavLink } from 'react-router-dom';
import {
  Users,
  Building2,
  FileText,
  BarChart3,
  Settings,
  MapPin,
  Table as TableIcon,
  CheckSquare,
  FolderKanban,
  LayoutTemplate,
  AlertTriangle,
  LogOut,
  Calendar,
  Calculator,
  Zap,
  Target,
  Construction,
  Plug,
  ChevronRight,
  Archive,
  Download,
  Crosshair,
  Heart,
  Briefcase,
  Globe,
  FileEdit,
  Image as ImageIcon,
  ArrowRightLeft,
  Beaker,
  Gauge,
  Presentation,
  LucideIcon,
  Map,
  Mail,
  Search,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useProfiles } from '@/hooks/useProfiles';
import { useSignedStorageUrl } from '@/hooks/useSignedStorageUrl';
import { useModuleSettings } from '@/hooks/useModuleSettings';
import { supabase } from '@/integrations/supabase/client';
import logoImage from '@/assets/greenvision-logo.png';

type SubItem = {
  icon: LucideIcon;
  label: string;
  href: string;
};

type MenuItem = {
  icon: LucideIcon;
  label: string;
  href: string;
  badge?: number;
  subItems?: SubItem[];
  moduleKey?: string;
};

type MenuSection = {
  label: string;
  items: MenuItem[];
};

// --- Actieve-navigatie-detectie (T1.7) --------------------------------------
// De sidebar matchte op `pathname === item.href`. Twee gevolgen: een item lichtte
// nooit op zodra je op een detailpagina stond (/crm/projects/:id) of de
// Nederlandse alias van een route gebruikte (/crm/projecten naast /crm/projects),
// en tegelijk stond Dashboard altijd aan, omdat NavLink zonder `end` óók op alle
// onderliggende paden matcht.
//
// Oplossing: normaliseer de alias weg en kies per render precies één winnaar —
// de langste href die op het huidige pad past. Zelfde principe als de
// route-ranking van React Router zelf: specifieker wint van algemener.
const ROUTE_ALIASES: Record<string, string> = {
  projecten: 'projects',
  contacten: 'contacts',
  klanten: 'organisaties',
  calendar: 'agenda',
  batterijen: 'battery-planner',
  gebruikers: 'users',
  'user-management': 'gebruikersbeheer',
  instellingen: 'settings',
};

const normalizePath = (path: string): string => {
  const segments = (path.replace(/\/+$/, '') || '/').split('/');
  // segment[1] is 'crm'; de alias zit altijd op segment[2].
  if (segments[1] === 'crm' && segments[2] && ROUTE_ALIASES[segments[2]]) {
    segments[2] = ROUTE_ALIASES[segments[2]];
  }
  return segments.join('/');
};

const CRM_ROOT = '/crm';

/** Ligt `href` op het huidige pad — als exacte match of als bovenliggend pad? */
const isOnPath = (pathname: string, href: string): boolean => {
  const current = normalizePath(pathname);
  const target = normalizePath(href);
  // Het dashboard is de wortel van élke CRM-route. Als prefix zou het overal
  // meelichten — precies de bug die het oude NavLink-gedrag had, want NavLink
  // matcht zonder `end` ook op onderliggende paden. Dus: alleen exact.
  if (target === CRM_ROOT) return current === target;
  return current === target || current.startsWith(`${target}/`);
};

/** De langste href die op het pad past, zodat er precies één item oplicht. */
const resolveActiveHref = (pathname: string, hrefs: string[]): string | null =>
  hrefs
    .filter((href) => isOnPath(pathname, href))
    .sort((a, b) => normalizePath(b).length - normalizePath(a).length)[0] ?? null;

export const AppSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasRole, signOut, userRole } = useAuth();
  const { currentProfile } = useProfiles();
  const { isModuleEnabled } = useModuleSettings();
  const [showLogoutButton, setShowLogoutButton] = useState(false);
  const { state } = useSidebar();

  // Prevent rendering CRM sidebar for charging users
  if (userRole === 'charging_user') {
    return null;
  }
  
  // Normalize avatar path for signed URL generation
  const avatarPath = currentProfile?.avatar_url?.startsWith('avatars/') 
    ? currentProfile.avatar_url 
    : currentProfile?.avatar_url ? `avatars/${currentProfile.avatar_url}` : null;
    
  const { url: avatarUrl } = useSignedStorageUrl('pictures', avatarPath);

  const isSuperAdmin = hasRole('super_admin');

  // Mail die op een besluit wacht (koppelsuggesties klaar), als getal naast Mail-triage.
  const { data: mailTeBeoordelen = 0 } = useQuery({
    queryKey: ['zijbalk', 'mail-te-beoordelen'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('email_inbox')
        .select('id', { count: 'exact', head: true })
        .eq('processing_status', 'suggestions_ready');
      return error ? 0 : count ?? 0;
    },
    staleTime: 60_000,
  });

  // Indeling, besluit van Stan 29-09-2026 (ADR 0019): alleen wat je dagelijks gebruikt staat
  // hier. To-do's (via het dashboard), Subsidies, Analyses, Flex-E, Laadplein Intelligence en
  // Intakes zijn bereikbaar via de zoekbalk (⌘K); Leads en Intelligence zijn weg tot ze weer
  // werken. De routes blijven bestaan.
  const menuSections: MenuSection[] = [
    {
      label: '',
      items: [
        { icon: BarChart3, label: 'Dashboard', href: '/crm' },
        { icon: Mail, label: 'Mail-triage', href: '/crm/mail', badge: mailTeBeoordelen },
        { icon: Calendar, label: 'Agenda', href: '/crm/agenda', moduleKey: 'calendar' },
      ]
    },
    {
      label: 'WERK',
      items: [
        { icon: FolderKanban, label: 'Projecten', href: '/crm/projects', moduleKey: 'projects' },
        { icon: FileText, label: 'Offertes', href: '/crm/quotes', moduleKey: 'quotes' },
        // F9.12, besluit van Stan 13-09-2026: Locaties is de enige ingang naar de
        // energiemonitor. Een Site met monitoring opent haar EMS op de sitepagina.
        { icon: MapPin, label: 'Locaties', href: '/crm/sites', moduleKey: 'sites' },
        { icon: Calculator, label: 'Businesscases', href: '/crm/businesscases', moduleKey: 'businesscases' },
        { icon: Map, label: 'Project Builder', href: '/project-builder' },
      ]
    },
    {
      label: 'CHARGING',
      items: [
        {
          icon: Zap,
          label: 'Charging',
          href: '/crm/charging',
          moduleKey: 'charging',
          subItems: [
            { icon: BarChart3, label: 'Dashboard', href: '/crm/charging' },
            { icon: Target, label: 'Acquisitie', href: '/crm/charging/acquisitie' },
            { icon: Construction, label: 'Realisatie', href: '/crm/charging/realisatie' },
            { icon: Plug, label: 'Operatie', href: '/crm/charging/operatie' },
            { icon: Briefcase, label: 'Opportunities', href: '/crm/charging/opportunities' },
            { icon: Crosshair, label: 'Concurrentie', href: '/crm/charging/concurrentie' },
            { icon: Heart, label: 'Wensen', href: '/crm/charging/wensen' },
            { icon: Presentation, label: 'Presentatie', href: '/crm/charging/presentatie' },
            { icon: Download, label: 'Export', href: '/crm/charging/export' },
            { icon: TableIcon, label: 'Tabel', href: '/crm/charging/gauw-tabel' },
            { icon: Archive, label: 'Archief', href: '/crm/charging/archief' },
          ]
        },
      ]
    },
    {
      label: 'RELATIES',
      items: [
        { icon: Building2, label: 'Organisaties', href: '/crm/organisaties', moduleKey: 'organisations' },
        { icon: Users, label: 'Contacten', href: '/crm/contacts', moduleKey: 'contacts' },
      ]
    },
    {
      label: 'MARKETING',
      items: [
        {
          // Het overzicht (ADR 0034) en greenvision.energy zelf (schema website, ADR 0024) staan
          // voorop; de planning (taken, tekstconcepten, notities, ideeën) hangt onder de laatste regel.
          icon: Globe,
          label: 'Website',
          href: '/crm/website',
          moduleKey: 'website',
          subItems: [
            { icon: Globe, label: 'Overzicht', href: '/crm/website' },
            { icon: FileEdit, label: 'Content op de site', href: '/crm/website/site' },
            { icon: FileText, label: "Pagina's", href: '/crm/website/site/paginas' },
            { icon: ImageIcon, label: 'Media', href: '/crm/website/site/media' },
            { icon: Settings, label: 'Instellingen', href: '/crm/website/site/instellingen' },
            { icon: ArrowRightLeft, label: 'Doorverwijzingen', href: '/crm/website/site/doorverwijzingen' },
            { icon: CheckSquare, label: 'Planning en taken', href: '/crm/website/planning' },
          ]
        },
      ]
    },
  ];

  /** Onderaan, los van het werk: archief en instellingen. */
  const onderMenu: MenuItem[] = [
    { icon: Archive, label: 'Archief', href: '/crm/archief' },
    { icon: Settings, label: 'Instellingen', href: '/crm/settings' },
  ];

  const adminMenuItems: MenuItem[] = [
    { icon: Users, label: 'Gebruikersbeheer', href: '/crm/gebruikersbeheer' },
    { icon: AlertTriangle, label: 'Foutmeldingen', href: '/crm/error-reports' },
    { icon: Gauge, label: 'Mail en AI', href: '/crm/mail-en-ai' },
    { icon: LayoutTemplate, label: 'Project Templates', href: '/crm/project-templates' },
    { icon: FileText, label: 'Handleidingen', href: '/crm/documentatie' },
    { icon: Beaker, label: 'Sandbox', href: '/crm/sandbox' },
  ];

  // Alle hrefs uit beide menu's, zodat een groepsitem niet meelicht met zijn
  // eigen subitem: de langste match wint.
  const activeHref = resolveActiveHref(location.pathname, [
    ...menuSections.flatMap((section) =>
      section.items.flatMap((item) => [item.href, ...(item.subItems?.map((sub) => sub.href) ?? [])]),
    ),
    ...adminMenuItems.map((item) => item.href),
    ...onderMenu.map((item) => item.href),
  ]);

  // Filter items based on module settings
  const filterItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter(item => {
      if (!item.moduleKey) return true;
      return isModuleEnabled(item.moduleKey);
    });
  };

  const getDisplayName = () => {
    return currentProfile?.display_name || user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Gebruiker';
  };

  const getInitials = () => {
    const displayName = getDisplayName();
    if (displayName && displayName !== 'Gebruiker') {
      return displayName.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const renderMenuItem = (item: MenuItem) => {
    if (item.subItems) {
      // De groep zelf opent en licht op zodra je érgens onder hem zit.
      const groupOnPath = isOnPath(location.pathname, item.href);
      return (
        <Collapsible 
          defaultOpen={groupOnPath}
          className="space-y-1 w-full"
        >
          <CollapsibleTrigger asChild>
            <SidebarMenuButton 
              isActive={groupOnPath}
              className="w-full justify-between group"
            >
              <div className="flex items-center gap-3">
                <item.icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent className="animate-in slide-in-from-top-1 duration-200">
            <div className="ml-6 space-y-1 py-1">
              {item.subItems.map((subItem) => (
                <SidebarMenuButton key={subItem.href} asChild isActive={activeHref === subItem.href}>
                  <NavLink 
                    to={subItem.href}
                    className={cn(
                      "flex items-center gap-3 text-sm py-2",
                      activeHref === subItem.href && "bg-accent text-accent-foreground"
                    )}
                  >
                    <subItem.icon className="h-3 w-3 shrink-0" />
                    <span>{subItem.label}</span>
                  </NavLink>
                </SidebarMenuButton>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    return (
      <SidebarMenuButton asChild isActive={activeHref === item.href}>
        <NavLink 
          to={item.href}
          className={cn(
            "flex items-center gap-3 w-full",
            activeHref === item.href && "bg-accent text-accent-foreground"
          )}
        >
          <div className="flex items-center gap-3 flex-1">
            <item.icon className="h-4 w-4" />
            <span>{item.label}</span>
          </div>
          {item.badge && item.badge > 0 && (
            <Badge variant="default" className="ml-auto h-5 min-w-5 px-1.5 text-xs">
              {item.badge}
            </Badge>
          )}
        </NavLink>
      </SidebarMenuButton>
    );
  };

  return (
    <Sidebar collapsible="offcanvas" className="border-r">
      <SidebarHeader className="p-4 border-b">
        <Link to="/crm" className="flex items-center gap-2">
          <img 
            src={`${logoImage}?v=${Date.now()}`}
            alt="GreenVision Energy Logo" 
            className="h-6 object-contain"
            loading="eager"
            decoding="async"
          />
        </Link>
      </SidebarHeader>
      
      <SidebarContent className="px-4">
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('crm:zoeken'))}
          className="mt-3 flex h-9 w-full items-center gap-2 rounded-md border bg-muted/40 px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Alles vinden: pagina's, projecten, offertes, taken"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">Zoeken</span>
          <kbd className="rounded border bg-background px-1.5 font-mono text-[10px]">⌘K</kbd>
        </button>
        {menuSections.map((section, sectionIndex) => {
          const filteredItems = filterItems(section.items);
          if (filteredItems.length === 0) return null;

          return (
            <SidebarGroup key={sectionIndex}>
              {section.label && (
                <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-2 mt-2 first:mt-0">
                  {section.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredItems.map((item) => (
                    <SidebarMenuItem key={item.href}>
                      {renderMenuItem(item)}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}

        {isSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-2 mt-2">
              Beheerder
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminMenuItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    {renderMenuItem(item)}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup className="mt-auto pb-2">
          <SidebarGroupContent>
            <SidebarMenu>
              {onderMenu.map((item) => (
                <SidebarMenuItem key={item.href}>
                  {renderMenuItem(item)}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* User Profile at Bottom */}
      {user && (
        <SidebarFooter className="p-4 border-t">
          <div 
            className="relative"
            onMouseEnter={() => setShowLogoutButton(true)}
            onMouseLeave={() => setShowLogoutButton(false)}
          >
            <div className="flex items-center gap-3">
              <Avatar 
                className="h-10 w-10" 
                style={{ backgroundColor: currentProfile?.avatar_bg_color || '#E5E7EB' }}
              >
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback 
                  style={{ backgroundColor: currentProfile?.avatar_bg_color || '#E5E7EB' }}
                  className="text-white font-medium text-sm"
                >
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              {state !== "collapsed" && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {getDisplayName()}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </p>
                </div>
              )}
            </div>
            
            {/* Hover Logout Button */}
            {showLogoutButton && (
              <div className="absolute top-0 right-0 bg-popover border border-border rounded-lg shadow-lg p-2 z-50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="h-4 w-4" />
                  {state !== "collapsed" && "Uitloggen"}
                </Button>
              </div>
            )}
          </div>
        </SidebarFooter>
      )}
    </Sidebar>
  );
};
